import createLatLngBounds from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/LatLngBounds';
import getAdriaticStyles from '@PROJECT_ROOT/assets/shared/js/map/GoogleMapsStyles';
import isGoogleLocationId from '@PROJECT_ROOT/assets/shared/js/PrivateAccommodation/googleLocationId';
import makeGetPlaceInfo from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/GooglePlaceInfo';
import makeShowFacilityDetails from '@PROJECT_ROOT/assets/mobile/js/mapSearch/FacilityDetails';
import MapSearch from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/MapSearch';
import openModal from '@PROJECT_ROOT/assets/shared/js/modalBox';
import openSyncWithMapDialog from '@PROJECT_ROOT/assets/mobile/js/mapSearch/syncWithMapDialog';
import { triggerGoogleAnalyticsSendEvent } from '@PROJECT_ROOT/assets/desktop/js/library/triggerGoogleAnalyticsSend';

export default function makeLoadSearchResultsMap(state) {
    let searchResultsMapCalled = false;
    let mapSearch;

    function loadSearchResultsMap(google, mapContainer) {
        if (!searchResultsMapCalled) {
            const map = createGoogleMapsMap(google, mapContainer);
            mapSearch = createMapSearch(map, state, google);
            const getPlaceInfo = makeGetPlaceInfo(
                new google.maps.places.PlacesService(map),
                google.maps.places.PlacesServiceStatus,
            );
            initMapSearchEvents(mapSearch, state, getPlaceInfo);
        }

        const closeMapModal = openModal(mapContainer, {
            overlayCss: ['private-mapsearch'],
            modalConentCss: ['private-mapsearch__content'],
            fullscreen: true,
            onBeforeClose: (modalContent, triggeredByUser) => {
                if (triggeredByUser) {
                    openSyncWithMapDialog(state.syncResultsWithMapDialogTexts, () => {
                        syncResultsWithMapSearch(mapSearch, state);
                        closeMapModal();
                    }, () => {
                        closeMapModal();
                    });
                }

                return { doNotCloseOnUserInteraction: true };
            },
        });

        searchResultsMapCalled = true;
    }

    return loadSearchResultsMap;
}

function createGoogleMapsMap(google, map) {
    return new google.maps.Map(
        map,
        {
            zoom: 8,
            minZoom: 7,
            center: { lat: 44.488, lng: 16.455 },
            scaleControl: true,
            fullscreenControl: false,
            styles: getAdriaticStyles(),
        },
    );
}

function createMapSearch(map, { mapSearchResultsURL, icons, facilityMarkerInfoURL, searchParameters }, google) {
    const elements = {
        mapSearchContainer: document.getElementById('searchResultsGoogleMap'),
    };

    const urls = {
        results: mapSearchResultsURL,
        facilityIcon: icons.facilityIcon,
        activeFacilityIcon: icons.facilityActiveIcon,
        facilitiesClusterIcon: icons.clusterIcon,
        activeFacilitiesClusterIcon: icons.clusterActiveIcon,
        facilityPriceSmallIcon: icons.facilitySmallPriceIcon,
        activeFacilityPriceSmallIcon: icons.activeFacilitySmallPriceIcon,
        facilityPriceMediumIcon: icons.facilityMediumPriceIcon,
        activeFacilityPriceMediumIcon: icons.activeFacilityMediumPriceIcon,
        placeIcon: icons.placeIcon,
    };

    const showFacilityDetails = makeShowFacilityDetails(map, facilityMarkerInfoURL, searchParameters, google);

    const options = {
        fetchFilterData(bounds) {
            return getFilterDataWithUpdatedCoordinates(bounds, searchParameters);
        },
        facilityMarkerClick: showFacilityDetails,
        onFacilityMarkerFocus: showFacilityDetails,
        getInitialViewport() {
            return getViewportCoordinates(searchParameters);
        },
    };

    return new MapSearch(map, elements, urls, triggerGoogleAnalyticsSendEvent, jQuery, options);
}

function initMapSearchEvents(mapSearch, { facilityMarkers, searchParameters, placeMapData }, getPlaceInfo) {
    mapSearch.onReady(() => {
        const locationId = searchParameters.location_id;
        const viewport = getViewportCoordinates(searchParameters);

        if (isGoogleLocationId(locationId)) {
            focusMapOnGoogleLocation(mapSearch, locationId, getPlaceInfo);
        } else if (placeMapData !== null) {
            mapSearch.focusMapOnLocation(placeMapData);
        } else if (isSearchByMap(searchParameters)) {
            mapSearch.searchOnViewport(viewport.getGoogleLiteral());
        } else if (searchParameters.nearMe) {
            mapSearch.focusMapOnLocation({
                latitude: parseFloat(searchParameters.coord_lat),
                longitude: parseFloat(searchParameters.coord_lng),
            });
        } else {
            mapSearch.setResults(facilityMarkers, /* shouldFitAllResults */ true);
        }
    });
}

function getFilterFormParametersSerialized(searchParameters) {
    return Object.keys(searchParameters).map((parameterName) => ({ name: parameterName, value: searchParameters[parameterName] }));
}

function getFilterDataWithUpdatedCoordinates(bounds, searchParameters) {
    const formParameters = getFilterFormParametersSerialized(searchParameters);

    if (bounds === null) {
        return formParameters;
    }

    return formParameters.map((parameter) => {
        if (parameter.name === 'ne_lat') {
            return { ...parameter, value: bounds.getNorthEast().lat() };
        }

        if (parameter.name === 'ne_lng') {
            return { ...parameter, value: bounds.getNorthEast().lng() };
        }

        if (parameter.name === 'sw_lat') {
            return { ...parameter, value: bounds.getSouthWest().lat() };
        }

        if (parameter.name === 'sw_lng') {
            return { ...parameter, value: bounds.getSouthWest().lng() };
        }

        return parameter;
    });
}

function getViewportCoordinates(searchParameters) {
    if (!areAllCoordinatesSet(searchParameters)) {
        return null;
    }

    return createLatLngBounds({
        north: searchParameters.ne_lat,
        east: searchParameters.ne_lng,
        south: searchParameters.sw_lat,
        west: searchParameters.sw_lng,
    });
}

function areAllCoordinatesSet(searchParameters) {
    return Boolean(searchParameters.sw_lat && searchParameters.sw_lng
        && searchParameters.ne_lat && searchParameters.ne_lng);
}

function focusMapOnGoogleLocation(mapSearch, locationId, getPlaceInfo) {
    return new Promise((resolve, reject) => {
        const googlePlaceFound = function focusMapOnGooglePlace(name, location, viewport) {
            mapSearch.focusMapOnLocation(location, viewport)
                .then(resolve)
                .fail((error) => {
                    reject(error);
                });
        };
        getPlaceInfo(locationId, googlePlaceFound, resolve);
    });
}

function isSearchByMap(searchParameters) {
    if (searchParameters.location || searchParameters.location_id) {
        return false;
    }

    return areAllCoordinatesSet(searchParameters);
}

function syncResultsWithMapSearch(mapSearch, { searchResultsURL, searchParameters }) {
    const filterParameters = getFilterDataWithUpdatedCoordinates(mapSearch.getBounds(), searchParameters);
    const $pageLoader = $('#pageLoader');
    let newFilterParameters = [];

    newFilterParameters = filterParameters.map((parameter) => {
        if (parameter.name === 'location') {
            return { ...parameter, value: '' };
        }

        if (parameter.name === 'location_id') {
            return { ...parameter, value: '' };
        }

        if (parameter.name === 'pl_r') {
            return { ...parameter, value: '' };
        }

        return parameter;
    });

    $pageLoader.show();

    $.get(searchResultsURL, newFilterParameters).then((response) => {
        window.location.href = response.searchResultsURL;
    }, () => {
        $pageLoader.hide();
    });
}
